// ============================================================================
// HyrjaEJashtme.jsx — I NJËJTI EKRAN HYRJEJE PËR FAQET JASHTË APLIKACIONIT
//
// 3 shtator 2026 · raportuar nga pronari:
//   «këtë e kam parë në dy vende — te "rreth nesh → nis familjen tënde" edhe kur
//    dërgon personal code invite, kur klikon në link shfaqet ky login. Logini
//    duhet të jetë i njëjti, ai i login-it të zakonshëm në fillim të faqes. Edhe
//    prapë nuk ka konsistencë te harmonizimi i ngjyrave: këtu "shkruaj emailin"
//    është e zezë, shkronjat janë të zeza.»
//
// ÇFARË ISHTE
//   `/start` (CreateFamily.jsx) dhe `/join` (JoinWithCode.jsx) janë dokumente më
//   vete — s'kalojnë fare nga App.jsx. Prandaj secili kishte formën e vet të
//   hyrjes, të shkruar me dorë: dy butona të bardhë, "ose", dy fusha të zhveshura
//   dhe një buton i zi. Asnjë lidhje me variantin që zgjedh admini te Admin Center.
//
//   FUSHA E ZEZË: `designLoader.js` vendos `color-scheme: dark` te <html> kur tema
//   është e errët. Kjo i thotë shfletuesit ta pikturojë çdo fushë TË PAPËRCAKTUAR
//   me sfond të errët. Ndërkohë preflight-i i Tailwind-it thotë `input { color:
//   inherit }`, dhe kartela e bardhë rreth saj kishte `color:#333`. Rezultati:
//   sfond i errët + shkronja të zeza. Asnjëra anë s'ishte gabim në vetvete — ato
//   thjesht s'e dinin për njëra-tjetrën. Prandaj çdo fushë këtu e shkruan VETË
//   sfondin dhe ngjyrën e vet; asgjë s'lihet në dorë të supozimit të shfletuesit.
//
// ÇFARË ËSHTË TANI
//   E njëjta kornizë `LoginScreen` si ballina — stema, titulli, sfondi, ngjyrat
//   dhe tekstet që i cakton admini — me një kartelë hyrjeje brenda që ndjek
//   pikërisht të njëjtat tokena. Kur admini ndërron variantin, ndërrohen edhe
//   këto dy faqe, pa i prekur më.
//
// PSE S'U NXOR `renderAuthFormBody` NGA App.jsx
//   Ajo formë mban gjendjen e ANËTARËSISË (familje të përputhshme, familje ku je
//   tashmë i lidhur, «llogaria jote s'është lidhur ende me këtë pemë») — koncepte
//   që këtu s'ekzistojnë: te `/start` s'ka ende familje, te `/join` familja
//   vendoset nga kodi. Nxjerrja do të kërkonte rreth 40 props dhe do të prekte
//   identifikimin e vetë aplikacionit ndërsa iOS-i është në shqyrtim. Kjo kartelë
//   mban VETËM derën e parë — Google, Apple, email — dhe atë e mban të plotë.
// ============================================================================

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LoginScreen, { FALLBACK_LOGIN } from "./LoginScreen.jsx";
import { goBackFromPage } from "./BackablePage.jsx";
import { supabase } from "./supabaseClient";
import { hyrMeOAuth, degjoKthiminEOAuth } from "./oauth.js";
import { shenoHyrjen } from "./gjurma.js"; // HYRJET1 — gjurma e derës // OAUTH1 — Google/Apple brenda app-it
import { getPasswordPolicy, validatePassword, passwordErrorText, signupsDisabledMsg } from "./passwordPolicy";

// I NJËJTI ÇELËS SI TE App.jsx — dhe kjo është pikërisht qëllimi. Ballina e ruan
// aty variantin e fundit që ktheu baza; këto faqe e lexojnë të njëjtin, ndaj
// hapen menjëherë me pamjen e duhur në vend që të pulsojnë me atë rezervë.
const LOGIN_CACHE = "pef.login.last";

// Pamja e ekranit të hyrjes, nga baza. Publike me qëllim: lexohet PARA se
// dikush të jetë identifikuar.
export function usePamjaEHyrjes() {
  const [settings, setSettings] = useState(() => {
    try { const raw = localStorage.getItem(LOGIN_CACHE); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  });
  useEffect(() => {
    let alive = true;
    supabase.rpc("rpc_get_login_settings").then(({ data, error }) => {
      if (!alive || error || !data) return;
      setSettings(data);
      try { localStorage.setItem(LOGIN_CACHE, JSON.stringify(data)); } catch (_e) { /* kuota / modaliteti privat */ }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return settings;
}

// Kodi i ftesës ruhet PARA se të nisë Google/Apple, dhe adresa e kthimit lihet
// e pastër. Arsyeja është një përplasje e vërtetë: Supabase-i kthehet me
// `?code=<pkce>` te adresa, dhe `/join` e ka tashmë `?code=<kodi i ftesës>` aty.
// Dy parametra me të njëjtin emër — dhe kush lexon i pari fiton. Me kodin te
// `sessionStorage` asnjëra anë s'ia merr vendin tjetrës.
export const RUAJTJA_E_KODIT = "pef_join_code";

/** Tokenat e pamjes së hyrjes (variantin e cakton admini) — për faqet jashtë App.jsx. */
export function useTokenatEHyrjes() {
  const settings = usePamjaEHyrjes();
  return { settings, tk: { ...FALLBACK_LOGIN.tokens, ...((settings && settings.tokens) || {}) } };
}

/**
 * KORNIZA E HYRJES (12 shtator 2026) — e njëjta ballinë (ilustrimi, stema,
 * kartela) edhe për faqet PAS identifikimit: /join (kodi, kodi personal,
 * kërkimi i familjes). Pronari: «kodi i familjes dhe kodi personal të duken si
 * faqja kryesore». HyrjaEJashtme e përdor po këtë për formën e hyrjes.
 */
export function KornizaEHyrjes({ titull, children }) {
  const { t } = useTranslation();
  const tr = (x) => (typeof x === "string" && x ? t(x, { defaultValue: x }) : x);
  const { settings, tk } = useTokenatEHyrjes();
  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", background: tk.bg }}>
      <LoginScreen settings={settings} tr={tr}>
        <button
          onClick={goBackFromPage}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", padding: "0 12px", margin: "-12px -12px -2px", minHeight: 44, color: tk.inkSoft, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
        >
          ← {t("Back", { defaultValue: "Kthehu" })}
        </button>
        {titull && (
          <h1 style={{ margin: "0 0 10px", fontSize: 16.5, fontWeight: 700, color: tk.ink, fontFamily: "'Playfair Display', Georgia, serif" }}>{titull}</h1>
        )}
        {children}
      </LoginScreen>
    </div>
  );
}

// `celesiTitullit` / `celesiHyrjes` janë ÇELËSA përkthimi, jo tekst i gatshëm —
// që faqja të flasë gjuhën e vizitorit. `vlerat` mbush {{familja}} / {{emri}}
// aty ku ftesa e di tashmë kujt i shkon.
export default function HyrjaEJashtme({ celesiTitullit, celesiHyrjes, vlerat }) {
  const { t } = useTranslation();
  const tr = (x) => (typeof x === "string" && x ? t(x, { defaultValue: x }) : x);
  const titull = celesiTitullit ? t(celesiTitullit, { ...(vlerat || {}), defaultValue: "" }) : "";
  const hyrje = celesiHyrjes ? t(celesiHyrjes, { ...(vlerat || {}), defaultValue: "" }) : "";
  const settings = usePamjaEHyrjes();
  const tk = { ...FALLBACK_LOGIN.tokens, ...((settings && settings.tokens) || {}) };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  async function handleOAuth(provider) {
    setError("");
    // Adresa e kthimit i ruan TË GJITHË parametrat e faqes (p.sh. `?token=` i
    // ftesës me email) përveç NJË-it: `code`. Atë e ruajmë veçmas, sepse
    // Supabase-i kthehet me `?code=<pkce>` të vetin — dy parametra me të njëjtin
    // emër, dhe kush lexon i pari fiton.
    let kthimi = window.location.origin + window.location.pathname;
    try {
      const q = new URLSearchParams(window.location.search);
      const c = (q.get("code") || "").trim();
      if (c) sessionStorage.setItem(RUAJTJA_E_KODIT, c);
      q.delete("code");
      const mbetja = q.toString();
      if (mbetja) kthimi += "?" + mbetja;
    } catch (_e) { /* modalitet privat — vazhdo me adresën e pastër */ }
    const e = await hyrMeOAuth(provider, kthimi);
    shenoHyrjen("oauth_start", { ok: !e, reason: e ? e.message : provider }); // HYRJET1
    if (e) setError(e.message);
  }
  // OAUTH1 — në native kthimi vjen me skemën e app-it edhe te këto faqe.
  useEffect(() => degjoKthiminEOAuth((g) => setError(g === "no_data" ? "" : g)), []);

  // HYRJE OSE REGJISTRIM, ME NJË BUTON — sjellja e vjetër e të dy faqeve, e
  // ruajtur fjalë për fjalë: provo hyrjen, dhe vetëm nëse dështon shiko a lejohen
  // regjistrimet dhe a e plotëson fjalëkalimi politikën.
  async function handleEmailAuth(e) {
    if (e) e.preventDefault();
    setError("");
    setBusy(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      const pol = await getPasswordPolicy();
      if (!pol.allow_signups) { shenoHyrjen("login_fail", { email, ok: false, reason: signInError.message }); setError(signupsDisabledMsg()); setBusy(false); return; }
      const pc = validatePassword(password, pol);
      if (!pc.ok) { shenoHyrjen("login_fail", { email, ok: false, reason: signInError.message }); setError(passwordErrorText(pc.errors)); setBusy(false); return; }
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email, password,
        options: { data: { lang: (navigator.language || "en").split("-")[0] } },
      });
      // HYRJET1 — hyrja dështoi dhe u provua regjistrimi (rruga e vjetër «një buton»)
      shenoHyrjen("signup_email", { email, ok: !signUpError, reason: signUpError ? signUpError.message : "after failed sign-in" });
      if (signUpError) { setError(signUpError.message); setBusy(false); return; }
      if (signUpData && signUpData.user && !signUpData.session) setNeedsEmailConfirm(true);
    }
    setBusy(false);
  }

  async function dergoRivendosjen() {
    if (!email.trim()) {
      setEmailOpen(true);
      setError(t("Enter your email first.", { defaultValue: "Shkruaj së pari email-in." }));
      return;
    }
    setError("");
    setBusy(true);
    const { error: e } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: window.location.origin + "/" });
    shenoHyrjen("password_reset", { email, ok: !e, reason: e ? e.message : null }); // HYRJET1
    setBusy(false);
    if (e) { setError(e.message); return; }
    setResetSent(true);
  }

  // ── Stilet, të gjitha nga tokenat e variantit ────────────────────────────
  // ÇDO fushë e shkruan vetë sfondin, ngjyrën dhe kufirin — shih shënimin lart
  // për fushën e zezë me shkronja të zeza.
  const fusha = {
    width: "100%", boxSizing: "border-box", borderRadius: 10,
    background: tk.bg2, color: tk.ink, border: `1px solid ${tk.cardBorder}`,
    fontSize: 14, fontFamily: "inherit", outline: "none",
  };
  const butoniAr = {
    width: "100%", padding: "11px", borderRadius: 10, background: tk.accent,
    color: tk.accentInk, border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer",
  };
  const butoniBardhe = {
    width: "100%", padding: "10px 14px", borderRadius: 10, background: "#fff", color: "#1f1f1f",
    border: "1.5px solid rgba(0,0,0,.10)", fontSize: 13.5, fontWeight: 600,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 9, cursor: "pointer",
  };
  const etiketa = { fontSize: 12.5, fontWeight: 600, letterSpacing: ".08em", color: tk.inkSoft, marginBottom: 4 };
  const ikonaEMajte = { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", display: "flex", color: tk.inkSoft };

  const trupi = needsEmailConfirm ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p style={{ margin: 0, fontSize: 13.5, color: tk.ink, lineHeight: 1.6 }}>
        {t("auth.checkEmailTitle", { email, defaultValue: `Të dërguam një link konfirmimi te ${email}. Hape atë, pastaj kthehu këtu.` })}
      </p>
      <button onClick={() => setNeedsEmailConfirm(false)} style={butoniAr}>{t("auth.backToSignIn", { defaultValue: "Kthehu te hyrja" })}</button>
    </div>
  ) : (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {hyrje && (
        <p style={{ margin: "0 0 2px", fontSize: 13, color: tk.inkSoft, lineHeight: 1.55, textAlign: "center" }}>{hyrje}</p>
      )}

      <div style={{ textAlign: "center", fontSize: 12.5, fontWeight: 600, letterSpacing: ".1em", color: tk.inkSoft }}>
        {t("auth.continueWith", { defaultValue: "VAZHDO ME" })}
      </div>

      <button onClick={() => handleOAuth("google")} style={butoniBardhe}>
        <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true"><path fill="#4285F4" d="M45 24.5c0-1.6-.1-2.7-.4-3.9H24v7.1h12c-.2 1.9-1.5 4.7-4.4 6.6l6.7 5.2c4-3.7 6.7-9.1 6.7-15z"/><path fill="#34A853" d="M24 46c5.9 0 10.8-1.9 14.4-5.3l-6.9-5.3c-1.8 1.3-4.3 2.2-7.5 2.2-5.7 0-10.6-3.8-12.3-9l-7.1 5.5C8.2 41.1 15.5 46 24 46z"/><path fill="#FBBC05" d="M11.7 28.6c-.4-1.3-.7-2.7-.7-4.1s.3-2.8.7-4.1l-7.1-5.5C3.6 17.6 3 20.7 3 24s.6 6.4 1.6 9.1z"/><path fill="#EA4335" d="M24 10.6c4 0 6.7 1.7 8.3 3.2l6.1-5.9C34.8 4.4 29.9 2 24 2 15.5 2 8.2 6.9 4.6 14.9l7.1 5.5c1.7-5.2 6.6-9.8 12.3-9.8z"/></svg>
        {t("auth.google", { defaultValue: "Vazhdo me Google" })}
      </button>

      <button onClick={() => handleOAuth("apple")} style={butoniBardhe}>
        <svg width="16" height="18" viewBox="0 0 384 512" fill="#000" aria-hidden="true"><path d="M318.7 268c-.2-37 16.6-64.9 50.6-85.5-19-27.2-47.7-42.2-85.6-45.1-35.9-2.8-75.1 21-89.5 21-15.2 0-49.9-20-77.2-20C61.2 139.1 0 184.6 0 277.2c0 27.3 5 55.5 15 84.5 13.3 38.2 61.3 132 111.4 130.4 26.2-.6 44.7-18.6 78.8-18.6 33.1 0 50.2 18.6 79.4 18.6 50.5-.7 93.9-86 106.6-124.3-67.7-31.9-72.5-93.5-72.5-99.8zM256.4 82.6c22.4-26.6 20.4-50.8 19.7-59.5-19.8 1.1-42.7 13.4-55.8 28.6-14.4 16.3-22.9 36.5-21.1 58.9 21.4 1.6 41-9.4 57.2-28z"/></svg>
        {t("auth.apple", { defaultValue: "Vazhdo me Apple" })}
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "2px 0" }}>
        <span style={{ flex: 1, height: 1, background: tk.cardBorder }} />
        <span style={{ fontSize: 13, color: tk.inkSoft }}>{t("auth.or", { defaultValue: "ose" })}</span>
        <span style={{ flex: 1, height: 1, background: tk.cardBorder }} />
      </div>

      {!emailOpen ? (
        <>
          {/* Një gabim i Google-it/Apple-it duhet parë KËTU, pa u hapur forma e
              email-it — përndryshe njeriu sheh dy fusha të reja dhe mendon se
              atij i kërkohet fjalëkalim. */}
          {error && <p style={{ margin: 0, fontSize: 12.5, color: "#F27A72" }}>{error}</p>}
          <button onClick={() => setEmailOpen(true)} style={butoniAr}>
            {t("auth.continueWithEmail", { defaultValue: "Vazhdo me Email" })}
          </button>
        </>
      ) : (
        <form onSubmit={handleEmailAuth} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div style={etiketa}>{t("auth.email", { defaultValue: "Email" }).toUpperCase()}</div>
            <div style={{ position: "relative" }}>
              <span style={ikonaEMajte}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5.5" width="18" height="13" rx="2"/><path d="M3.5 7l8.5 6 8.5-6"/></svg>
              </span>
              <input
                type="email" autoComplete="email" required
                placeholder={t("auth.emailPlaceholder", { defaultValue: "email@shembull.com" })}
                value={email} onChange={(e) => setEmail(e.target.value)}
                style={{ ...fusha, padding: "10px 12px 10px 36px" }}
              />
            </div>
          </div>

          <div>
            <div style={etiketa}>{t("auth.password", { defaultValue: "Fjalëkalimi" }).toUpperCase()}</div>
            <div style={{ position: "relative" }}>
              <span style={ikonaEMajte}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="4.5" y="10.5" width="15" height="9.5" rx="2"/><path d="M8.2 10.5V8a3.8 3.8 0 0 1 7.6 0v2.5"/></svg>
              </span>
              <input
                type={showPw ? "text" : "password"} autoComplete="current-password" required minLength={6}
                placeholder={t("auth.passwordPlaceholder", { defaultValue: "Fjalëkalimi juaj" })}
                value={password} onChange={(e) => setPassword(e.target.value)}
                style={{ ...fusha, padding: "10px 38px 10px 36px" }}
              />
              <button
                type="button" onClick={() => setShowPw((v) => !v)}
                title={showPw ? t("Hide", { defaultValue: "Fshihe" }) : t("Show", { defaultValue: "Shfaqe" })}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: tk.inkSoft, display: "flex", padding: 2 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="3"/>
                  {showPw && <path d="M4 20 20 4"/>}
                </svg>
              </button>
            </div>
          </div>

          {error && <p style={{ margin: 0, fontSize: 12.5, color: "#F27A72" }}>{error}</p>}

          <button type="submit" disabled={busy} style={{ ...butoniAr, opacity: busy ? 0.6 : 1, marginTop: 2 }}>
            {busy ? t("auth.pleaseWait", { defaultValue: "…" }) : t("auth.continueWithEmail", { defaultValue: "Vazhdo me Email" })}
          </button>

          <div style={{ textAlign: "center" }}>
            {resetSent ? (
              <span style={{ fontSize: 12.5, color: tk.inkSoft }}>
                {t("We sent you a link. Open it and choose a new password.", { defaultValue: "Të dërguam një link. Hape dhe zgjidh një fjalëkalim të ri." })}
              </span>
            ) : (
              <button type="button" disabled={busy} onClick={dergoRivendosjen}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12.5, color: tk.accent, padding: 0 }}>
                {t("Forgot your password?", { defaultValue: "Harrove fjalëkalimin?" })}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );

  // Korniza (ilustrimi, stema, kartela, «← Kthehu») është e përbashkët — shih KornizaEHyrjes.
  return <KornizaEHyrjes titull={titull}>{trupi}</KornizaEHyrjes>;
}
